import { ServiceData, TESTIMONIAL_DATA, TestimonialData } from "@/lib/data";
import SecondaryButton from "@/components/ui/secondary-button";
import { SERVICE_DATA } from "@/lib/data";
import Testimonial from "@/components/ui/testimonial";
import { cn } from "@/lib/utils";
import Image from "next/image";
import WbencLogo from "@/assets/images/locations/WBENC 1.png";
import ProviderLogo from "@/assets/images/locations/BOHC Provider 1.png";

const careersLogos = [
  {
    src: WbencLogo,
    alt: "Certified WBENC - Women's Business Enterprise",
  },
  {
    src: ProviderLogo,
    alt: "Best of Home Care - Employer of Choice 2024",
  },
];

export interface AwardsTestimonialsProps {
  id?: string;
  heading: string;
  subheading: string;
  description: string;
  showSecondaryCTA: boolean;
  secondaryCta?: {
    text: string;
    href: string;
  };
  data?: ServiceData[];
  overrideLogos?: boolean;
  logos?: { src: string; alt: string }[];
  testimonials?: TestimonialData[];
}

const defaultProps: AwardsTestimonialsProps = {
  heading: "Heading",
  subheading: "Sub Heading",
  description: "Sample description",
  showSecondaryCTA: false,
  secondaryCta: {
    href: "/",
    text: "Search Jobs",
  },
  data: SERVICE_DATA,
  testimonials: TESTIMONIAL_DATA,
};

const AwardsTestimonials: React.FC<AwardsTestimonialsProps> = ({
  id = "awards-testimonials",
  heading,
  subheading,
  description,
  showSecondaryCTA,
  secondaryCta,
  data = SERVICE_DATA,
  overrideLogos,
  logos,
  testimonials = TESTIMONIAL_DATA,
}) => {
  heading = heading ?? defaultProps.heading;
  subheading = subheading ?? defaultProps.subheading;
  description = description ?? defaultProps.description;
  showSecondaryCTA = showSecondaryCTA ?? defaultProps.showSecondaryCTA;
  secondaryCta = secondaryCta ?? defaultProps.secondaryCta;
  const logoList = !overrideLogos ? careersLogos : (logos ?? []);

  return (
    <section
      id={id}
      className="bg-white"
    >
      <div className="flex flex-col gap-4 p-4 md:gap-6 md:px-6 md:py-8 lg:mx-auto lg:max-w-lg lg:gap-8 lg:px-3 lg:py-10">
        <div className="flex flex-col gap-4 lg:gap-6">
          <hgroup className="flex flex-col gap-2 md:gap-3 lg:gap-4">
            <h2 className="font-montserrat text-heading-4 text-secondary-500 md:text-heading-3 lg:text-heading-2">
              {heading}
            </h2>
            <h3 className="font-montserrat text-subheading-3-400 text-neutral-500 lg:text-subheading-2">
              {subheading}
            </h3>
          </hgroup>
          <p className="font-roboto text-body-1-300 text-neutral-500">
            {description}
          </p>
          {showSecondaryCTA && (
            <div className="flex">
              <SecondaryButton
                color="gray"
                size="md"
                transparent
                href={secondaryCta?.href}
                className="flex"
              >
                {secondaryCta?.text}
              </SecondaryButton>
            </div>
          )}
        </div>
        <div className="flex flex-row md:gap-6 lg:gap-8">
          <div className="flex w-full flex-row gap-8 whitespace-nowrap md:justify-normal lg:gap-14">
            {data?.map((datum, index) => (
              <Data
                key={`${datum.label}-${index}`}
                count={datum.count}
                label={datum.label}
              />
            ))}
          </div>
          <div className="hidden w-full flex-row items-center justify-end gap-4 md:flex md:h-16 lg:h-18 lg:gap-10">
            {logoList.map(({ src, alt }) => (
              <div
                key={"career-logo-" + alt}
                className="relative"
              >
                <Image
                  src={src}
                  alt={alt}
                  width={500}
                  height={500}
                  className="w-auto object-contain md:h-16 lg:h-18"
                />
              </div>
            ))}
          </div>
        </div>
        <div
          className={cn(
            "flex w-full flex-col gap-2 md:flex-row md:gap-4 lg:col-start-2 lg:row-span-full lg:justify-between",
            "[&>*:nth-child(1)]:flex [&>*:nth-child(2)]:flex [&>*:nth-child(3)]:hidden",
            "md:[&>*:nth-child(1)]:flex md:[&>*:nth-child(2)]:flex md:[&>*:nth-child(3)]:hidden",
            "lg:[&>*:nth-child(1)]:flex lg:[&>*:nth-child(2)]:flex lg:[&>*:nth-child(3)]:flex"
          )}
        >
          {testimonials?.map((testimonial, index) => (
            <Testimonial
              key={`${testimonial.name}-${index}`}
              className={"bg-white"}
              variant="stars"
              stars={testimonial.stars}
              name={testimonial.name}
              date={testimonial.date}
              imageURL={testimonial.imageURL}
            >
              {testimonial?.text}
            </Testimonial>
          ))}
        </div>
      </div>
    </section>
  );
};

const Data: React.FC<ServiceData> = ({ count, label }) => {
  return (
    <div className="text-start">
      <p className="font-montserrat text-heading-4 text-primary-500 md:text-heading-2">
        {count.toLocaleString()}+
      </p>
      <p className="font-roboto text-body-2-300 text-neutral-500 md:text-body-1-300">
        {label}
      </p>
    </div>
  );
};

export default AwardsTestimonials;
